import React, { useState, useMemo } from 'react';
import { useContent } from '../../hooks/useContent';
import { guidelinesAuthorsEn } from '../../content/guidelinesAuthorsEn';
import styles from './GuidelinesForAuthors.module.css';

const CARD_IDS = ['presentations', 'submissionLimits', 'language', 'originalityScope'];
const ITEM_KEYS = ['item1', 'item2', 'item3'];

const EXPANDABLE_IDS = [
  'reviewCriteria',
  'reviewProcess',
  'lengthRequirements',
  'anonymization',
  'publicationFees',
  'journalPublication',
  'copyright',
  'warranties',
  'selfCitation',
  'originalityCheck',
];

const BULLET_KEYS = [
  'bullet1',
  'bullet2',
  'bullet3',
  'bullet4',
  'bullet5',
  'bullet6',
  'bullet7',
  'bullet8',
  'bullet9',
  'bullet10',
];

const BODY_ONLY_IDS = new Set(['selfCitation', 'originalityCheck']);

const CardIcon = ({ variant }) => {
  if (variant === 0) {
    return (
      <svg className={styles.cardIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
        <line x1="9" y1="3" x2="9" y2="21" />
      </svg>
    );
  }
  if (variant === 1) {
    return (
      <svg className={styles.cardIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
        <polyline points="7 10 12 15 17 10" />
        <line x1="12" y1="15" x2="12" y2="3" />
      </svg>
    );
  }
  if (variant === 2) {
    return (
      <svg className={styles.cardIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
      </svg>
    );
  }
  return (
    <svg className={styles.cardIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  );
};

const GuidelinesForAuthors = () => {
  const { t } = useContent();
  const [expandedSections, setExpandedSections] = useState({});

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const def = guidelinesAuthorsEn;

  const cardItems = (cardId) => {
    const d = def.cards[cardId] || {};
    return ITEM_KEYS.map((ik) => t(`guidelinesAuthors.cards.${cardId}.${ik}`, d[ik] ?? '')).filter((x) =>
      String(x).trim(),
    );
  };

  const expandableBullets = (sectionId) => {
    const d = def.expandable[sectionId] || {};
    return BULLET_KEYS.map((bk) =>
      t(`guidelinesAuthors.expandable.${sectionId}.${bk}`, d[bk] ?? ''),
    ).filter((x) => String(x).trim());
  };

  const cards = useMemo(
    () =>
      CARD_IDS.map((id, idx) => {
        const c = def.cards[id] || {};
        return {
          id,
          variant: idx,
          title: t(`guidelinesAuthors.cards.${id}.title`, c.title ?? ''),
          subtitle: t(`guidelinesAuthors.cards.${id}.subtitle`, c.subtitle ?? ''),
          items: cardItems(id),
        };
      }),
    [t],
  );

  return (
    <div className={styles.container}>
      <h1 className={styles.mainTitle}>{t('guidelinesAuthors.pageTitle', def.pageTitle)}</h1>

      <div className={styles.cardsGrid}>
        {cards.map((card) => (
          <div key={card.id} className={styles.card}>
            <div className={styles.cardHeader}>
              <CardIcon variant={card.variant} />
              <div>
                <h3 className={styles.cardTitle}>{card.title}</h3>
                {card.subtitle ? <p className={styles.cardSubtitle}>{card.subtitle}</p> : null}
              </div>
            </div>
            {card.items.length > 0 ? (
              <ul className={styles.cardList}>
                {card.items.map((text, i) => (
                  <li key={`${card.id}-li-${i}`}>{text}</li>
                ))}
              </ul>
            ) : null}
          </div>
        ))}
      </div>

      <div className={styles.expandableContainer}>
        {EXPANDABLE_IDS.map((sectionId) => {
          const ex = def.expandable[sectionId] || {};
          const title = t(`guidelinesAuthors.expandable.${sectionId}.title`, ex.title ?? '');
          const intro = t(`guidelinesAuthors.expandable.${sectionId}.intro`, ex.intro ?? '');
          const body = t(`guidelinesAuthors.expandable.${sectionId}.body`, ex.body ?? '');
          const bullets = expandableBullets(sectionId);
          const bodyOnly = BODY_ONLY_IDS.has(sectionId);

          return (
            <div key={sectionId} className={styles.expandableSection}>
              <button
                type="button"
                className={styles.expandableHeader}
                onClick={() => toggleSection(sectionId)}
              >
                <span className={styles.expandableTitle}>{title}</span>
                <svg
                  className={`${styles.chevron} ${expandedSections[sectionId] ? styles.chevronOpen : ''}`}
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </button>
              {expandedSections[sectionId] && (
                <div className={styles.expandableContent}>
                  {bodyOnly ? (
                    <p>{body}</p>
                  ) : (
                    <>
                      {String(intro).trim() ? <p>{intro}</p> : null}
                      {bullets.length > 0 ? (
                        <ul>
                          {bullets.map((line, i) => (
                            <li key={`${sectionId}-b-${i}`}>{line}</li>
                          ))}
                        </ul>
                      ) : null}
                    </>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default GuidelinesForAuthors;
